import React from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'hooks/redux'
import { useMakeDispatchCallback } from 'hooks'
import { getIsEditing } from 'selectors/navigator'
import { setSelectedIdWithDrawer } from 'actions/navigator'
import { compose } from 'ramda'
import strings from 'utils/strings'
import { classNames } from 'utils/utils'
import { SelectMenu } from 'components/patterns/Inputs'
import { LowerNavButton } from 'components/feature/NavigatorPanes/editors/Editors.style'

import { schemaProp } from 'schemas/validation'
import outputMapSchema from 'schemas/validation/feature/outputMapSchema'

import styled from '@emotion/styled'
import {
  makeGetNewLogicNodeIndex,
  getInputRestrictionFromOutput,
} from 'selectors/feature/logicNodes'
import { COMPLEX, TIMER } from 'utils/constants.js'
import { useDispatch } from 'react-redux'
import { addLogicNodeToOutput } from 'actions/feature/logicNodes.js'
import { LIMITED, RESTRICTED } from 'selectors/feature/utils.js'
import { themeColor } from 'utils/styles.js'
import withStyle, { StyledLabel } from './OutputQuery.style.js'

const rule = schemaProp(outputMapSchema)

const OutputQuery = ({
  fnId,
  id,
  className,
  outputQueryDescription,
  uri,
  updateOutputQuery,
  validUris,
  isSingleConditionOutput,
  isOneOutputQueryDefined,
}) => {
  const setSelectedIdCallback = useMakeDispatchCallback(setSelectedIdWithDrawer)

  const isEditing = useSelector(getIsEditing)
  const restrictions = useSelector(getInputRestrictionFromOutput, { id })
  const renderLinks = !isEditing || isSingleConditionOutput

  const handleOutputChange = (val) => {
    if (val) {
      updateOutputQuery(val)
    }
  }

  const link = (
    <LowerNavButton link onClick={() => setSelectedIdCallback(uri)}>
      {outputQueryDescription}
    </LowerNavButton>
  )

  // add handling for create logic set, create timer
  const canAddLogicSet = restrictions !== LIMITED
  const canAddTimer = restrictions !== RESTRICTED

  const defaults = [
    ...(canAddLogicSet
      ? [
          {
            key: 'create_logic_set',
            title: <CreateNewLogicSet fnId={fnId} />,
          },
        ]
      : []),
    ...(canAddTimer
      ? [
          {
            key: 'create_timer',
            title: <CreateNewTimer fnId={fnId} />,
          },
        ]
      : []),
  ]
  const options = validUris.map((option) => ({
    key: option.uri,
    value: option.uri,
    title: option.description,
  }))

  const selectMenu = (
    <SelectMenu
      vertical
      value={uri}
      label={strings.carry_from}
      name={strings.carry_from}
      onChange={handleOutputChange}
      options={[...defaults, ...options]}
      placeholder={strings.select_output_query}
      showSearch
      rules={rule('outputQuery', { isOneOutputQueryDefined })}
      filterOption
      data-testid="carry-from-select"
    />
  )

  return (
    <div className={classNames(className, 'ouput-query')}>
      {renderLinks ? (
        <div>
          <StyledLabel text={strings.input_data_source} weight="bold" />
          {link}
        </div>
      ) : (
        selectMenu
      )}
    </div>
  )
}

OutputQuery.propTypes = {
  /** Classname */
  className: PropTypes.string,
  outputQueryDescription: PropTypes.string,
  /** Whether the output takes input from a 16-bit CAN */
  isSixteenBitCan: PropTypes.bool,
  /** uri of the output query */
  uri: PropTypes.string,
  /** handler for changing the output query */
  updateOutputQuery: PropTypes.func,
  /** which queries may be used as the output query */
  validUris: PropTypes.array,
  isSingleConditionOutput: PropTypes.bool,
  // is at least one output query defined
  isOneOutputQueryDefined: PropTypes.bool,
  fnId: PropTypes.string,
  id: PropTypes.string,
}

export default compose(withStyle)(OutputQuery)

const StyledDropdownLink = styled.div`
  color: ${themeColor('bluePrimary')};
`

const CreateNewLogicSet = ({ fnId }) => {
  const dispatch = useDispatch()
  const getNewLogicSetIndex = makeGetNewLogicNodeIndex(COMPLEX)
  const nextIndex = useSelector(getNewLogicSetIndex)

  const handleClick = () => {
    dispatch(addLogicNodeToOutput(COMPLEX, nextIndex, fnId))
  }

  return (
    <StyledDropdownLink onClick={handleClick}>
      + {strings.create_logic_set}
    </StyledDropdownLink>
  )
}

CreateNewLogicSet.propTypes = {
  fnId: PropTypes.string,
}

const CreateNewTimer = ({ fnId }) => {
  const dispatch = useDispatch()
  const getNewTimerIndex = makeGetNewLogicNodeIndex(TIMER)

  const nextIndex = useSelector(getNewTimerIndex)

  const handleClick = () => {
    dispatch(addLogicNodeToOutput(TIMER, nextIndex, fnId))
  }

  return (
    <StyledDropdownLink onClick={handleClick}>
      + {strings.create_timer}
    </StyledDropdownLink>
  )
}

CreateNewTimer.propTypes = {
  fnId: PropTypes.string,
}
